import type { Pollutant } from "../model/inp/pollutants.ts";
import type { Treatment, TreatmentKind } from "../model/inp/treatment.ts";
import { useT } from "../i18n/hooks.ts";
import { TrashIcon } from "./TrashIcon.tsx";

/** The treatment expression is opaque free text, unlike Buildup/Washoff's fixed numeric coefficients — this app has no water-quality engine to evaluate it against. */
export function TreatmentEditor({
  nodeName,
  pollutants,
  treatments,
  onChange,
}: {
  nodeName: string;
  pollutants: Pollutant[];
  treatments: Treatment[];
  onChange: (rows: Treatment[]) => void;
}) {
  const t = useT();
  const usedNames = new Set(treatments.map((tr) => tr.pollutant));
  const availableToAdd = pollutants.filter((p) => !usedNames.has(p.name));

  function addRow() {
    if (availableToAdd.length === 0) return;
    onChange([
      ...treatments,
      { node: nodeName, pollutant: availableToAdd[0].name, kind: "R", expression: "0" },
    ]);
  }

  function setPollutant(oldPollutant: string, pollutant: string) {
    onChange(treatments.map((tr) => (tr.pollutant === oldPollutant ? { ...tr, pollutant } : tr)));
  }

  function setKind(pollutant: string, kind: TreatmentKind) {
    onChange(treatments.map((tr) => (tr.pollutant === pollutant ? { ...tr, kind } : tr)));
  }

  function setExpression(pollutant: string, expression: string) {
    onChange(treatments.map((tr) => (tr.pollutant === pollutant ? { ...tr, expression } : tr)));
  }

  function removeRow(pollutant: string) {
    onChange(treatments.filter((tr) => tr.pollutant !== pollutant));
  }

  if (pollutants.length === 0) {
    return (
      <section className="property-panel-group">
        <h3>{t("propertyPanel.treatment")}</h3>
        <p className="property-panel-hint">{t("projectData.noPollutantsYet")}</p>
      </section>
    );
  }

  return (
    <section className="property-panel-group">
      <h3>{t("propertyPanel.treatment")}</h3>
      {treatments.length === 0 && (
        <p className="property-panel-hint">{t("propertyPanel.noTreatments")}</p>
      )}
      {treatments.length > 0 && (
        <div className="named-collection-points-table-wrap">
          <table className="named-collection-points-table">
            <thead>
              <tr>
                <th>{t("projectData.pollutant")}</th>
                <th>{t("propertyPanel.treatmentKind")}</th>
                <th>{t("propertyPanel.treatmentExpression")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {treatments.map((treatment) => (
                <tr key={treatment.pollutant}>
                  <td>
                    <select
                      value={treatment.pollutant}
                      onChange={(e) => setPollutant(treatment.pollutant, e.target.value)}
                    >
                      <option value={treatment.pollutant}>{treatment.pollutant}</option>
                      {availableToAdd.map((p) => (
                        <option key={p.name} value={p.name}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={treatment.kind}
                      onChange={(e) =>
                        setKind(treatment.pollutant, e.target.value as TreatmentKind)
                      }
                    >
                      <option value="R">{t("propertyPanel.treatmentKindRemoval")}</option>
                      <option value="C">{t("propertyPanel.treatmentKindConcentration")}</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={treatment.expression}
                      onChange={(e) => setExpression(treatment.pollutant, e.target.value)}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="button-icon button-icon-danger"
                      onClick={() => removeRow(treatment.pollutant)}
                      aria-label={t("propertyPanel.deleteTreatment")}
                      title={t("propertyPanel.deleteTreatment")}
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button className="button" onClick={addRow} disabled={availableToAdd.length === 0}>
        {t("propertyPanel.addTreatment")}
      </button>
    </section>
  );
}
